import { AxiosResponse } from 'axios';
import { apiClient } from '../apiClient';
import { Contact } from '../../types';
import { EmergencyContact } from '../../types/EmergencyContact';

/**
 * Service for contact-related API calls
 */
export const contactService = {
  /**
   * Get contacts for a device
   * @param deviceId - The device ID
   */
  getContacts: (deviceId: string): Promise<AxiosResponse<Contact[]>> => {
    return apiClient.get(`/api/devices/${deviceId}/contacts`);
  },

  /**
   * Get emergency contacts for a device
   * @param deviceId - The device ID
   */
  getEmergencyContacts: (deviceId: string): Promise<AxiosResponse<EmergencyContact[]>> => {
    return apiClient.get(`/api/devices/${deviceId}/emergency-contacts`);
  },

  /**
   * Create a new contact
   * @param deviceId - The device ID
   * @param contact - The contact data
   */
  createContact: (deviceId: string, contact: Contact): Promise<AxiosResponse<Contact>> => {
    return apiClient.post(`/api/devices/${deviceId}/contacts`, contact);
  },

  /**
   * Update an existing contact
   * @param deviceId - The device ID
   * @param contactId - The contact ID
   * @param contact - The updated contact data
   */
  updateContact: (
    deviceId: string,
    contactId: string,
    contact: Contact,
  ): Promise<AxiosResponse<Contact>> => {
    return apiClient.put(`/api/devices/${deviceId}/contacts/${contactId}`, contact);
  },

  /**
   * Delete a contact
   * @param deviceId - The device ID
   * @param contactId - The contact ID
   */
  deleteContact: (deviceId: string, contactId: string): Promise<AxiosResponse> => {
    return apiClient.delete(`/api/devices/${deviceId}/contacts/${contactId}`);
  },

  /**
   * Invite a contact
   * @param deviceId - The device ID
   * @param contact - The contact data
   */
  inviteContact: (deviceId: string, contact: Contact): Promise<AxiosResponse<Contact>> => {
    return apiClient.post(`/api/devices/${deviceId}/invite`, contact);
  },

  /**
   * Create a new emergency contact
   * @param deviceId - The device ID
   * @param emergencyContact - The emergency contact data
   */
  createEmergencyContact: (
    deviceId: string,
    emergencyContact: EmergencyContact,
  ): Promise<AxiosResponse<EmergencyContact>> => {
    return apiClient.post(`/api/devices/${deviceId}/emergency-contacts`, emergencyContact);
  },

  /**
   * Update an existing emergency contact
   * @param deviceId - The device ID
   * @param emergencyContactId - The emergency contact ID
   * @param emergencyContact - The updated emergency contact data
   */
  updateEmergencyContact: (
    deviceId: string,
    emergencyContactId: string,
    emergencyContact: EmergencyContact,
  ): Promise<AxiosResponse<EmergencyContact>> => {
    return apiClient.put(
      `/api/devices/${deviceId}/emergency-contacts/${emergencyContactId}`,
      emergencyContact,
    );
  },

  /**
   * Delete an emergency contact
   * @param deviceId - The device ID
   * @param emergencyContactId - The emergency contact ID
   */
  deleteEmergencyContact: (
    deviceId: string,
    emergencyContactId: string,
  ): Promise<AxiosResponse> => {
    return apiClient.delete(`/api/devices/${deviceId}/emergency-contacts/${emergencyContactId}`);
  },
};
